import { useTranslation } from "react-i18next";
import { CONTACT_PHONE_DISPLAY } from "@/mocks/siteData";
import { waLink, telLink } from "@/lib/links";
import BrandLogo from "@/components/feature/BrandLogo";

export default function Footer() {
  const { t } = useTranslation();

  const socials = [
    { icon: "ri-whatsapp-line", label: "WhatsApp", href: waLink(t("wa.footer")) },
    { icon: "ri-facebook-fill", label: "Facebook", href: "https://facebook.com" },
    { icon: "ri-instagram-line", label: "Instagram", href: "https://instagram.com" },
    { icon: "ri-telegram-line", label: "Telegram", href: "https://t.me/" },
  ];

  const serviceLinks = [
    { label: t("footer.bike"), href: "#fleet" },
    { label: t("footer.scooter"), href: "#fleet" },
    { label: t("footer.car"), href: "#fleet" },
    { label: t("footer.serviceCenter"), href: "#service" },
    { label: t("footer.spare"), href: "#service" },
  ];

  const companyLinks = [
    { label: t("footer.how"), href: "#how" },
    { label: t("footer.pricing"), href: "#pricing" },
    { label: t("footer.faq"), href: "#faq" },
    { label: t("footer.contact"), href: "#contact" },
  ];

  return (
    <footer className="bg-foreground-950 text-background-50 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <BrandLogo variant="light" />
            <p className="mt-5 text-background-50/70 text-sm leading-relaxed">{t("footer.about")}</p>
            <div className="mt-6 flex items-center gap-3">
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={social.label}
                  className="w-10 h-10 rounded-full bg-background-50/10 hover:bg-primary-500 flex items-center justify-center transition cursor-pointer"
                >
                  <i className={`${social.icon} text-lg w-5 h-5 flex items-center justify-center`} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-heading font-bold uppercase tracking-wider text-xs mb-5">
              <a href="#services" className="cursor-pointer hover:text-primary-400 transition">
                {t("footer.services")}
              </a>
            </h4>
            <ul className="space-y-3 text-sm text-background-50/70">
              {serviceLinks.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="hover:text-primary-400 transition cursor-pointer">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-bold uppercase tracking-wider text-xs mb-5">
              <a href="#home" className="cursor-pointer hover:text-primary-400 transition">
                {t("footer.company")}
              </a>
            </h4>
            <ul className="space-y-3 text-sm text-background-50/70">
              {companyLinks.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="hover:text-primary-400 transition cursor-pointer">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-bold uppercase tracking-wider text-xs mb-5">
              <a href="#contact" className="cursor-pointer hover:text-primary-400 transition">
                {t("footer.contact")}
              </a>
            </h4>
            <ul className="space-y-4 text-sm text-background-50/70">
              <li className="flex items-start gap-3">
                <i className="ri-map-pin-2-fill text-primary-400 mt-0.5 w-4 h-4 flex items-center justify-center" />
                <span>
                  {t("contact.addressLine")}
                  <br />
                  {t("contact.addressCity")}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-phone-fill text-primary-400 mt-0.5 w-4 h-4 flex items-center justify-center" />
                <a href={telLink} className="hover:text-primary-400 transition cursor-pointer">
                  {CONTACT_PHONE_DISPLAY}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-whatsapp-line text-primary-400 mt-0.5 w-4 h-4 flex items-center justify-center" />
                <a
                  href={waLink(t("wa.footer"))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary-400 transition cursor-pointer"
                >
                  {t("footer.whatsappChat")}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-time-line text-primary-400 mt-0.5 w-4 h-4 flex items-center justify-center" />
                <span>{t("footer.hours")}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-background-50/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
          <p className="text-xs text-background-50/60">{t("footer.rights")}</p>
          <p className="text-xs text-background-50/60 uppercase tracking-widest">{t("footer.curated")}</p>
        </div>
      </div>
    </footer>
  );
}